// ABOUTME: Renders optional Cloudflare verification for social sign-in without private metadata.
// ABOUTME: Clears expired tokens, exposes retries, and removes the widget when the page changes.
import { useEffect, useRef, useState } from "react";
import { message } from "../api";
import { Button, Notice } from "./ui/controls";

type TurnstileApi = {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      action: "oauth_login";
      theme: "light";
      size: "flexible" | "compact";
      "response-field": false;
      "refresh-expired": "auto";
      callback: (token: string) => void;
      "expired-callback": () => void;
      "error-callback": () => void;
      "timeout-callback": () => void;
      "unsupported-callback": () => void;
    },
  ) => string | undefined;
  remove: (widgetId: string) => void;
};
const challengeWindow = window as typeof window & { turnstile?: TurnstileApi };
let scriptRequest: Promise<TurnstileApi> | null = null;

function loadTurnstile() {
  if (challengeWindow.turnstile)
    return Promise.resolve(challengeWindow.turnstile);
  if (scriptRequest) return scriptRequest;
  scriptRequest = new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement("script");
    script.src =
      "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    const fail = () => {
      clearTimeout(timeout);
      script.remove();
      scriptRequest = null;
      reject(
        new Error(
          "Sign-in verification could not load. Check your connection and retry.",
        ),
      );
    };
    const timeout = setTimeout(fail, 15_000);
    script.addEventListener("error", fail, { once: true });
    script.addEventListener(
      "load",
      () => {
        clearTimeout(timeout);
        if (challengeWindow.turnstile) resolve(challengeWindow.turnstile);
        else fail();
      },
      { once: true },
    );
    document.head.appendChild(script);
  });
  return scriptRequest;
}

export function TurnstileChallenge({
  siteKey,
  onTokenChange,
}: {
  siteKey: string;
  onTokenChange: (token: string) => void;
}) {
  const container = useRef<HTMLDivElement>(null);
  const [revision, setRevision] = useState(0);
  const [status, setStatus] = useState("Preparing sign-in verification…");
  const [error, setError] = useState("");
  useEffect(() => {
    let alive = true;
    let widgetId: string | undefined;
    onTokenChange("");
    setError("");
    setStatus("Preparing sign-in verification…");
    void loadTurnstile()
      .then((api) => {
        if (!alive || !container.current) return;
        const failed = (text: string) => {
          if (!alive) return;
          onTokenChange("");
          setStatus("");
          setError(text);
        };
        setStatus(
          "Complete the verification to continue with a sign-in provider.",
        );
        widgetId = api.render(container.current, {
          sitekey: siteKey,
          action: "oauth_login",
          theme: "light",
          size: container.current.clientWidth >= 300 ? "flexible" : "compact",
          "response-field": false,
          "refresh-expired": "auto",
          callback: (token) => {
            if (!alive) return;
            onTokenChange(token);
            setError("");
            setStatus("Verification complete. Choose your sign-in provider.");
          },
          "expired-callback": () => {
            if (!alive) return;
            onTokenChange("");
            setStatus(
              "Verification expired. Complete the renewed check to continue.",
            );
          },
          "error-callback": () =>
            failed("Verification could not complete. Please retry."),
          "timeout-callback": () =>
            failed("Verification timed out. Please retry."),
          "unsupported-callback": () =>
            failed(
              "This browser cannot run sign-in verification. Try another browser.",
            ),
        });
        if (widgetId === undefined)
          throw new Error(
            "Sign-in verification could not start. Please retry.",
          );
      })
      .catch((reason) => {
        if (!alive) return;
        onTokenChange("");
        setStatus("");
        setError(message(reason));
      });
    return () => {
      alive = false;
      if (widgetId !== undefined) challengeWindow.turnstile?.remove(widgetId);
    };
  }, [siteKey, revision, onTokenChange]);
  return (
    <div className="turnstile-challenge">
      <div ref={container} className="turnstile-widget" />
      {status && (
        <p className="fine-print" role="status">
          {status}
        </p>
      )}
      {error && (
        <Notice
          kind="error"
          action={
            <Button
              variant="secondary"
              onClick={() => setRevision((value) => value + 1)}
            >
              Retry verification
            </Button>
          }
        >
          {error}
        </Notice>
      )}
    </div>
  );
}
